import React, { useEffect, useState } from "react";
import { MapRef } from "react-map-gl";
import Tooltip from "@mui/material/Tooltip";
import { ZoomControl } from "./ZoomControl";
import { PlaybackSpeedButton } from "./PlaybackSpeedButton";
import { AdditionalActionsContainer, StyledButton } from "./AdditionalActionsStyle";
import WilmaSupportDialog from "../WILMASupportDialog";
import TripSupportDialog from "../../trips/TripSupportDialog";
import { useForecastTime } from "../../../features/surface/surfaceSlice";
import { formatForecastTime } from "../../../features/surface/surfaceUtils";
import { RouteRequestState } from "../../../generated/gpb/api/route";
import {
    OPEN_ROADS_EMPTY,
    OPEN_ROADS_LOADING,
    OPEN_ROADS_STOPS,
    SUPPORT_HEADER,
    TOGGLE_FULLSCREEN_OFF,
    TOGGLE_FULLSCREEN_ON,
    USER_TIPS,
    WIND_STREAMLINES_TOGGLE_OFF,
    WIND_STREAMLINES_TOGGLE_ON
} from "../../UserVisibleStrings";
import { useIsRightBarExpanded } from "../../../features/Drawer/drawerSlice";
import { faCompress, faExpand, faGasPump, faInfo, faQuestion, faWind, } from "@fortawesome/sharp-duotone-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIsMobile } from "../../../features/pageWidth/pageWidthSlice";
import { useRiskViewerState } from "../../../features/riskViewer/riskViewerSlice";
import { useAppDispatch } from "../../../app/hooks";
import {
    OpenRoadsStatus,
    toggleIsShowingWindStreamlines,
    toggleOpenRoadsVisibility,
    useIsShowingWindStreamlines,
    useOpenRoadsStatus,
    useOpenRoadsVisibility
} from "../../../features/mapDisplay/mapDisplaySlice";
import { PRETTY_BLUE } from "../../trips/form/BorderWithLabel";
import { useShouldShowAlerts } from "../../../features/alertDisplay/alertDisplaySlice";

type AdditionalActionsProps = {
    mapRef: React.RefObject<MapRef>,
};

/**
 * Component providing additional actions like zoom control, fullscreen toggle, and support dialogs.
 * @returns JSX.Element
 */
export const AdditionalActions: React.FC<AdditionalActionsProps> = ( {
    mapRef,
} ) =>
{
    const [ isFullscreen, setIsFullscreen ] = useState( false );
    const [ openHelp, setOpenHelp ] = useState( false );
    const riskViewerState = useRiskViewerState();
    const tripData = riskViewerState.tripState;
    const isRightBarExpanded = useIsRightBarExpanded();
    const openRoadsVisibility: boolean = useOpenRoadsVisibility();
    const openRoadsStatus = useOpenRoadsStatus();
    const isMobile = useIsMobile();
    const dispatch = useAppDispatch();
    const routeId = riskViewerState.selectedRoute;
    const routeRequestInput = tripData?.routeRequestInput;
    const isMultistartSelectionState = RouteRequestState.SHOW_OPTIONS.valueOf() === tripData?.routeState;
    const isShowingAlerts = useShouldShowAlerts();
    const isShowingWindStreamlines = useIsShowingWindStreamlines();

    const getOpenRoadsTooltip = () =>
    {
        switch ( openRoadsStatus )
        {
            case OpenRoadsStatus.Empty:
                return OPEN_ROADS_EMPTY;
            case OpenRoadsStatus.Loaded:
                return OPEN_ROADS_STOPS;
            default:
                return OPEN_ROADS_LOADING;
        }
    };

    const isOpenRoadsEnabled = openRoadsStatus === OpenRoadsStatus.Loaded;

    const toggleFullscreen = (): void =>
    {
        if ( !document.fullscreenElement )
        {
            void document.documentElement.requestFullscreen();
            setIsFullscreen( true );
        }
        else
        {
            void document.exitFullscreen();
            setIsFullscreen( false );
        }
    };

    useEffect( () =>
    {
        const handleFullscreenChange = () =>
        {
            setIsFullscreen( !!document.fullscreenElement );
        };

        document.addEventListener( "fullscreenchange", handleFullscreenChange );
        return () =>
        {
            document.removeEventListener( "fullscreenchange", handleFullscreenChange );
        };
    }, [] );

    const forecastTimeData = useForecastTime();
    const formattedForecastTime = formatForecastTime( forecastTimeData?.date, forecastTimeData?.hour );


    return (
        <AdditionalActionsContainer isMobile = {isMobile} isRightBarExpanded = {isRightBarExpanded} isShowingAlerts = {isShowingAlerts} id = "step4">
            {!isShowingAlerts && <PlaybackSpeedButton/>}
            {!isMobile && (
                <React.Fragment>
                    <ZoomControl mapRef = {mapRef}/>
                    <Tooltip title = {isFullscreen ? TOGGLE_FULLSCREEN_OFF : TOGGLE_FULLSCREEN_ON}>
                        <StyledButton onClick = {toggleFullscreen}>
                            {isFullscreen ? <FontAwesomeIcon icon = {faCompress} size = "sm" fixedWidth/> : <FontAwesomeIcon icon = {faExpand} size = "sm" fixedWidth/>}
                        </StyledButton>
                    </Tooltip>
                </React.Fragment>
            )}
            <Tooltip title = {USER_TIPS}>
                <StyledButton href = "https://rvweather.com/user-tips/?">
                    <FontAwesomeIcon icon = {faInfo} size = "sm" fixedWidth/>
                </StyledButton>
            </Tooltip>
            <Tooltip title = {SUPPORT_HEADER}>
                <StyledButton onClick = {() => setOpenHelp( true )}>
                    <FontAwesomeIcon icon = {faQuestion} size = "sm" fixedWidth/>
                </StyledButton>
            </Tooltip>
            {tripData !== undefined && routeId !== undefined && !isMultistartSelectionState && !isShowingAlerts && (
                <Tooltip title = {getOpenRoadsTooltip()}>
                    <span>
                        <StyledButton
                            onClick = {() => dispatch( toggleOpenRoadsVisibility() )}
                            disabled = {!isOpenRoadsEnabled}
                        >
                            <FontAwesomeIcon
                                icon = {faGasPump}
                                size = "sm"
                                fixedWidth
                                color = {openRoadsVisibility ? PRETTY_BLUE : "Grey"}
                            />
                        </StyledButton>
                    </span>
                </Tooltip>
            )}
            {!isShowingAlerts && (
                <Tooltip title = {isShowingWindStreamlines ? WIND_STREAMLINES_TOGGLE_OFF : WIND_STREAMLINES_TOGGLE_ON}>
                    <span>
                        <StyledButton
                            onClick = {() => dispatch( toggleIsShowingWindStreamlines() )}
                        >
                            <FontAwesomeIcon
                                icon = {faWind}
                                size = "sm"
                                fixedWidth
                                color = {isShowingWindStreamlines ? "red" : "Grey"}
                            />
                        </StyledButton>
                    </span>
                </Tooltip> )}
            {formattedForecastTime.length > 0 && routeId === undefined ? (
                <WilmaSupportDialog
                    forecastTime = {formattedForecastTime}
                    open = {openHelp}
                    onClose = {() => setOpenHelp( false )}
                />
            ) : (
                openHelp && (
                    <TripSupportDialog
                        helpInfo = {{
                            trip: tripData,
                            tripID: tripData?.Id,
                            routeID: routeId,
                            input: routeRequestInput,
                            formattedForecastTime: formattedForecastTime,
                        }}
                        open = {openHelp}
                        onClose = {() => setOpenHelp( false )}
                    />
                )
            )}
        </AdditionalActionsContainer>
    );
};

export default AdditionalActions;
